// src/components/StripeConnectCard.tsx
import { useTranslation } from "react-i18next";
import { resolveUiLanguage } from "../i18n/languages";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { A, pillClass } from "../styles/appleClasses";
import { usePostHog } from "@posthog/react";

type StripeLocalErrorKey = `stripeConnect.errors.${keyof typeof import("../i18n/locales/en.json").stripeConnect.errors}`;

// Explicit provenance: local copy rerenders in the current locale; external text
// keeps the original message-only precedence (no HTTP detail extraction).
class LocalStripeError extends Error {
  constructor(readonly key: StripeLocalErrorKey) {
    super();
  }
}
type StripeError = LocalStripeError | { source: "raw"; text: string };
function localStripeError(key: StripeLocalErrorKey) {
  return new LocalStripeError(key);
}
function stripeError(error: any, fallback: StripeLocalErrorKey): StripeError {
  if (error instanceof LocalStripeError) return error;
  return error?.message
    ? { source: "raw", text: error.message }
    : localStripeError(fallback);
}

type Props = {
  onConnected?: () => void | Promise<void>;
  onMethodsChanged?: () => void | Promise<void>;
};

type CapsResp = {
  stripe: {
    enabled: boolean;
    live_mode: boolean;
    methods: string[];
    connect_account_id?: string | null;
  };
};

type StripeStatusResp = {
  connected: boolean;
  account_id: string | null;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  can_onboard?: boolean;
  account_type?: string | null;
};

type ConnectStartResp = {
  url?: string;
  connected?: boolean;
  account_id?: string | null;
};

type Account = {
  id: number;
  name: string;
  kind?: string | null;
  type?: string | null;
};

type PaymentMethodRead = {
  id: number;
  name: string;
  type: string;
  fee_rate?: number;
  fixed_fee?: number;
  account_id: number;
  company_id?: number;
  provider?: string;
  provider_method?: string;
  online?: boolean;
};

const STRIPE_SETTINGS_DEPOSIT_KEY =
  "rb_settings_pending_stripe_deposit_account";

function isStripePm(pm: Partial<PaymentMethodRead> | null | undefined) {
  const name = String(pm?.name || "")
    .trim()
    .toLowerCase();

  return (
    pm?.provider === "stripe" ||
    pm?.online === true ||
    name === "stripe" ||
    name === "stripe link"
  );
}

function isSelectableDepositAccount(a: Account) {
  const kind = String(a.kind || "").toLowerCase();
  const type = String(a.type || "").toLowerCase();
  const name = String(a.name || "")
    .trim()
    .toLowerCase();

  if (name === "cash_system" || name === "bank_system") return false;

  if (kind === "cash" || kind === "bank") return true;

  if (
    type === "asset" &&
    (name.includes("cash") || name.includes("bank")) &&
    !name.endsWith("_system")
  ) {
    return true;
  }

  return false;
}

export default function StripeConnectCard({
  onConnected,
  onMethodsChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const feeSeparator = resolveUiLanguage(i18n.resolvedLanguage ?? i18n.language) === "zh-HK" ? "：" : ": ";
  const posthog = usePostHog();
  const location = useLocation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id?: string }>();

  function companyQuery() {
    return company_id ? `?company_id=${company_id}` : "";
  }

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<StripeError | null>(null);

  const [gatewayEnabled, setGatewayEnabled] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [connectedAcct, setConnectedAcct] = useState<string | null>(null);
  const [accountType, setAccountType] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRead[]>([]);
  const [stripeDepositAccountId, setStripeDepositAccountId] =
    useState<string>("");

  const [showConnectModal, setShowConnectModal] = useState(false);

  const connectedFiredRef = useRef(false);

  const inTestMode = useMemo(
    () => gatewayEnabled && !liveMode,
    [gatewayEnabled, liveMode],
  );

  const connected = !!connectedAcct;

  const depositAccounts = useMemo(
    () => accounts.filter(isSelectableDepositAccount),
    [accounts],
  );

  const selectedDepositAccount =
    depositAccounts.find((a) => String(a.id) === stripeDepositAccountId) ||
    null;

  const stripeDepositName =
    depositAccounts.find((a) => String(a.id) === stripeDepositAccountId)
      ?.name || "";

  async function tryUpdatePaymentMethod(pmId: number, payload: object) {
    const url = `/api/payment-methods/${pmId}${companyQuery()}`;
    const methods = ["PUT", "PATCH", "POST"] as const;

    let lastText = "";

    for (const method of methods) {
      try {
        const r = await fetch(url, {
          method,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (r.ok) return true;
        lastText = await r.text().catch(() => "");
      } catch {
        // try next verb
      }
    }

    throw (lastText ? new Error(lastText) : localStripeError("stripeConnect.errors.updateMethod"));
  }

  async function syncStripeDepositAccount(accountId: number) {
    const freshMethods = await apiGet<PaymentMethodRead[]>(
      `/payment-methods${companyQuery()}`,
    );

    const stripeMethods = freshMethods.filter((pm) => isStripePm(pm));

    const desired = [
      {
        name: "Stripe",
        type: "digital",
        fee_rate: 0,
        fixed_fee: 0,
        account_id: accountId,
        provider: "stripe",
        provider_method: "auto",
        online: true,
      },
      {
        name: "Stripe Link",
        type: "digital",
        fee_rate: 0,
        fixed_fee: 0,
        account_id: accountId,
        provider: "stripe",
        provider_method: "paylink",
        online: true,
      },
    ];

    for (const def of desired) {
      const existing = stripeMethods.find((pm) => {
        const method = String(pm.provider_method || "").trim().toLowerCase();
        const name = String(pm.name || "").trim().toLowerCase();

        return (
          method === def.provider_method.toLowerCase() ||
          name === def.name.toLowerCase()
        );
      });

      if (existing) {
        const needsUpdate =
          existing.account_id !== accountId ||
          existing.provider !== def.provider ||
          existing.provider_method !== def.provider_method ||
          existing.online !== def.online ||
          existing.type !== def.type;

        if (needsUpdate) {
          await tryUpdatePaymentMethod(existing.id, {
            name: def.name,
            type: def.type,
            fee_rate: 0,
            fixed_fee: 0,
            account_id: accountId,
            provider: def.provider,
            provider_method: def.provider_method,
            online: def.online,
          });
        }
      } else {
        await apiPost(`/payment-methods${companyQuery()}`, def);
      }
    }

    const refreshed = await apiGet<PaymentMethodRead[]>(
      `/payment-methods${companyQuery()}`,
    );

    setPaymentMethods(refreshed);
    await Promise.resolve(onMethodsChanged?.()).catch(() => void 0);
  }

  async function fetchAll() {
    setLoading(true);
    setErr(null);

    try {
      const [capRes, statusRes, acctRes, pmRes] = await Promise.allSettled([
        apiGet<CapsResp>("/payments/capabilities"),
        apiGet<StripeStatusResp>("/stripe/status"),
        apiGet<Account[]>(`/accounts${companyQuery()}`),
        apiGet<PaymentMethodRead[]>(`/payment-methods${companyQuery()}`),
      ]);

      if (capRes.status === "fulfilled") {
        const caps = capRes.value;
        setGatewayEnabled(!!caps.stripe.enabled);
        setLiveMode(!!caps.stripe.live_mode);

        if (typeof caps.stripe.connect_account_id === "string") {
          setConnectedAcct(caps.stripe.connect_account_id);
        }
      }

      if (statusRes.status === "fulfilled") {
        const s = statusRes.value;
        setConnectedAcct(s.account_id ?? null);
        setAccountType(s.account_type ?? null);
      }

      let fetchedAccounts: Account[] = [];

      if (acctRes.status === "fulfilled") {
        fetchedAccounts = acctRes.value || [];
        setAccounts(fetchedAccounts);
      }

      if (pmRes.status === "fulfilled") {
        const methods = pmRes.value || [];
        setPaymentMethods(methods);

        const stripePm = methods.find((pm) => isStripePm(pm));
        const pending =
          sessionStorage.getItem(STRIPE_SETTINGS_DEPOSIT_KEY) || "";

        if (pending) {
          setStripeDepositAccountId(pending);
        } else if (stripePm?.account_id) {
          const matched = fetchedAccounts.find(
            (a) =>
              String(a.id) === String(stripePm.account_id) &&
              isSelectableDepositAccount(a),
          );

          setStripeDepositAccountId(matched ? String(matched.id) : "");
        } else {
          setStripeDepositAccountId("");
        }
      }
    } catch (e: any) {
      setErr(stripeError(e, "stripeConnect.errors.loadStatus"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  useEffect(() => {
    if (connected && !connectedFiredRef.current) {
      connectedFiredRef.current = true;

      posthog.capture("stripe_connected", {
        company_id,
        account_id: connectedAcct,
        account_type: accountType,
        live_mode: liveMode,
      });

      Promise.resolve(onConnected?.()).catch(() => void 0);
    }
  }, [connected, connectedAcct, accountType, liveMode, company_id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeFlag = params.get("stripe");

    if (stripeFlag !== "done" && stripeFlag !== "refresh") return;

    posthog.capture("stripe_connect_returned", {
      company_id,
      status: stripeFlag,
    });

    let cancelled = false;

    void (async () => {
      try {
        const pending =
          sessionStorage.getItem(STRIPE_SETTINGS_DEPOSIT_KEY) || "";

        await fetchAll();

        if (!cancelled && pending) {
          setStripeDepositAccountId(pending);
          await syncStripeDepositAccount(Number(pending));
          sessionStorage.removeItem(STRIPE_SETTINGS_DEPOSIT_KEY);
        }
      } catch (e: any) {
        if (!cancelled) {
          setErr(stripeError(e, "stripeConnect.errors.syncDeposit"));
        }
      } finally {
        if (!cancelled) {
          const cleaned = new URLSearchParams(location.search);
          cleaned.delete("stripe");

          navigate(
            `${location.pathname}${
              cleaned.toString() ? `?${cleaned.toString()}` : ""
            }`,
            { replace: true },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, navigate, company_id]);

  const confirmConnect = async () => {
    if (busy) return;

    if (!stripeDepositAccountId) {
      setErr(localStripeError("stripeConnect.errors.chooseDeposit"));
      return;
    }

    setBusy(true);
    setErr(null);

    try {
      sessionStorage.setItem(
        STRIPE_SETTINGS_DEPOSIT_KEY,
        stripeDepositAccountId,
      );

      posthog.capture("stripe_connect_started", {
        company_id,
        deposit_account_id: stripeDepositAccountId,
      });

      const res = await apiPost<ConnectStartResp>("/stripe/connect/start", {});

      if (res?.connected) {
        await fetchAll();
        await syncStripeDepositAccount(Number(stripeDepositAccountId));
        setShowConnectModal(false);
        return;
      }

      if (!res?.url) {
        throw localStripeError("stripeConnect.errors.missingUrl");
      }

      window.location.href = res.url;
    } catch (e: any) {
      setErr(stripeError(e, "stripeConnect.errors.start"));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (busy) return;

    setBusy(true);
    setErr(null);

    const tryPost = async (path: string, body: any = {}) => {
      try {
        await apiPost(path, body);
        return true;
      } catch {
        return false;
      }
    };

    try {
      if (await tryPost("/stripe/disconnect")) {
        sessionStorage.removeItem(STRIPE_SETTINGS_DEPOSIT_KEY);
        await fetchAll();
        await Promise.resolve(onMethodsChanged?.()).catch(() => void 0);
        return;
      }

      throw localStripeError("stripeConnect.errors.disconnectEndpoint");
    } catch (e: any) {
      setErr(stripeError(e, "stripeConnect.errors.disconnect"));
    } finally {
      setBusy(false);
    }
  };

  const tone = connected ? (inTestMode ? "warning" : "success") : "neutral";

  const statusText = connected
    ? inTestMode
      ? t("stripeConnect.status.connectedTest")
      : t("stripeConnect.status.connected")
    : t("stripeConnect.status.notConnected");

  return (
    <>
      <div className={`${A.card} w-full max-w-md p-5 mb-4`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className={A.title}>Stripe</h3>
          <span className={pillClass(tone)}>{statusText}</span>
        </div>

        {!!err && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-[13px] font-normal mb-3">
            {err instanceof LocalStripeError ? t(err.key) : err.text}
          </div>
        )}

        <p className={`${A.body} text-gray-500 mb-3`}>
          {t("stripeConnect.description")}
        </p>

        {loading ? (
          <div className="inline-flex items-center gap-2 text-[13px] font-normal text-gray-500">
            <span className="h-4 w-4 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin" />
            {t("directory.common.loading")}
          </div>
        ) : !connected ? (
          <>
            <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="text-xs font-medium text-gray-700 mb-1">
                {t("stripeConnect.fees.title")}
              </div>

              <div className="space-y-1 text-xs text-gray-500">
                <div>
                  <span>{t("stripeConnect.fees.processing")}</span>{feeSeparator}<span>{t("stripeConnect.fees.pricing")}</span>
                </div>

                <div>
                  <span>{t("stripeConnect.fees.platform")}</span>{feeSeparator}<span>0.1%</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className={`${A.btnBase} ${A.btnPrimary}`}
              onClick={() => {
                setErr(null);

                posthog.capture("stripe_connect_clicked", {
                  company_id,
                  source: "stripe_card",
                });

                setShowConnectModal(true);
              }}
              disabled={busy}
            >
              {t("stripeConnect.connect")}
            </button>
          </>
        ) : (
          <>
            <div className={`mt-2 text-xs text-gray-500 ${A.num}`}>
              {t("stripeConnect.account")}{" "}
              <span className="font-mono text-gray-600">{connectedAcct}</span>
              {" • "}
              {inTestMode ? t("stripeConnect.testMode") : t("stripeConnect.liveMode")}
              {accountType ? ` • ${accountType}` : ""}
            </div>

            <div className="mt-2 text-xs text-gray-500">
              {t("stripeConnect.depositsInto")}{" "}
              <span className="font-medium text-gray-700">
                {stripeDepositName || t("stripeConnect.notSelected")}
              </span>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="text-xs font-medium text-gray-700 mb-1">
                {t("stripeConnect.fees.title")}
              </div>

              <div className="space-y-1 text-xs text-gray-500">
                <div>
                  <span>{t("stripeConnect.fees.processing")}</span>{feeSeparator}<span>{t("stripeConnect.fees.pricing")}</span>
                </div>

                <div>
                  <span>{t("stripeConnect.fees.platform")}</span>{feeSeparator}<span>0.1%</span>
                </div>
              </div>
            </div>


            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                onClick={disconnect}
                disabled={busy}
                className={`${A.btnBase} ${A.btnSecondary}`}
              >
                {t("stripeConnect.disconnect")}
              </button>
            </div>

            <div className="text-xs text-gray-500 mt-3">
              {t("stripeConnect.manageMethods")}
            </div>
          </>
        )}
      </div>

      {showConnectModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className={`${A.card} w-full max-w-md shadow-md`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className={A.title}>{t("stripeConnect.dialog.title")}</h3>

              <button
                type="button"
                onClick={() => {
                  if (busy) return;
                  setShowConnectModal(false);
                }}
                className={`${A.iconBtn} inline-flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100`}
                aria-label={t("directory.common.close")}
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    d="M6 6l8 8M14 6l-8 8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="text-[13px] text-gray-700">
                {t("stripeConnect.dialog.chooseDeposit")}
              </div>

              <div>
                <label className="block mb-1 text-xs text-gray-500">
                  {t("stripeConnect.dialog.depositAccount")}
                </label>

                <AppleComboBox
                  className="w-full"
                  options={depositAccounts}
                  value={selectedDepositAccount}
                  onChange={(opt) => {
                    setStripeDepositAccountId(opt ? String(opt.id) : "");
                    setErr(null);
                  }}
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  getOptionLabel={(opt) => opt.name}
                  placeholder={
                    depositAccounts.length
                      ? t("stripeConnect.dialog.selectAccount")
                      : t("stripeConnect.dialog.noAccounts")
                  }
                />

                <div className="mt-1 text-xs text-gray-500">
                  {t("stripeConnect.dialog.accountsHelp")}
                </div>
              </div>

              {depositAccounts.length === 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700 text-[13px]">
                  {t("stripeConnect.dialog.addAccountFirst")}
                </div>
              )}

              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  {t("stripeConnect.fees.title")}
                </div>

                <div className="space-y-1 text-xs text-gray-500">
                  <div>
                    <span>{t("stripeConnect.fees.processing")}</span>{feeSeparator}<span>{t("stripeConnect.fees.pricing")}</span>
                  </div>

                  <div>
                    <span>{t("stripeConnect.fees.platform")}</span>{feeSeparator}<span>0.1%</span>
                  </div>
                </div>

                <div className="mt-2 text-[11px] text-gray-400 leading-snug">
                  {t("stripeConnect.dialog.secureProcessing")}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={`${A.btnBase} ${A.btnSecondary}`}
                  onClick={() => setShowConnectModal(false)}
                  disabled={busy}
                >
                  {t("directory.common.cancel")}
                </button>

                <button
                  type="button"
                  className={`${A.btnBase} ${A.btnPrimary}`}
                  onClick={confirmConnect}
                  disabled={
                    busy ||
                    !stripeDepositAccountId ||
                    depositAccounts.length === 0
                  }
                >
                  {busy ? t("stripeConnect.dialog.connecting") : t("stripeConnect.dialog.continue")}
                </button>
              </div>

              <div className="text-[11px] text-gray-400 leading-snug">
                {t("stripeConnect.dialog.offlinePayments")}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}